import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, CloudOff, RefreshCw } from 'lucide-react';
import { subscribeToSWState } from '../services/pwa';

interface OfflineIndicatorProps {
  className?: string;
  showWhenOnline?: boolean;
}

export function OfflineIndicator({ className = '', showWhenOnline = false }: OfflineIndicatorProps) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showIndicator, setShowIndicator] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToSWState((state) => {
      const newIsOffline = state.isOffline;

      if (!newIsOffline && isOffline) {
        // Just came back online
        setWasOffline(true);
        setTimeout(() => setWasOffline(false), 3000);
      }

      setIsOffline(newIsOffline);
      setShowIndicator(newIsOffline || (showWhenOnline && wasOffline));
    });

    return unsubscribe;
  }, [isOffline, showWhenOnline, wasOffline]);

  // Show reconnected message briefly when coming back online
  if (wasOffline && !isOffline) {
    return (
      <div className={`fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-auto z-40 ${className}`}>
        <div className="bg-green-600 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-fade-in">
          <Wifi className="w-5 h-5" />
          <span className="font-medium">Back online</span>
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm opacity-80">Syncing...</span>
        </div>
      </div>
    );
  }

  if (!isOffline) {
    return null;
  }

  return (
    <div className={`fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-auto z-40 ${className}`}>
      <div className="bg-amber-600 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3">
        <WifiOff className="w-5 h-5" />
        <div className="flex-1">
          <p className="font-medium">You're offline</p>
          <p className="text-sm opacity-80">Changes will sync when you reconnect</p>
        </div>
        <CloudOff className="w-5 h-5 opacity-60" />
      </div>
    </div>
  );
}

// Mini offline indicator for the navbar/header
export function OfflineIndicatorMini({ className = '' }: { className?: string }) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const unsubscribe = subscribeToSWState((state) => {
      setIsOffline(state.isOffline);
    });

    return unsubscribe;
  }, []);

  if (!isOffline) {
    return null;
  }

  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 bg-amber-500/20 text-amber-500 rounded-lg text-sm ${className}`}>
      <WifiOff className="w-3.5 h-3.5" />
      <span className="font-medium">Offline</span>
    </div>
  );
}

export default OfflineIndicator;
